import { AWARD_CODES } from "../../enums/award-codes";
import { DROPDOWN_LABELS, DROPDOWN_KEYS, ROUTE_PATHS, ROUTE_LABELS, ROUTE_VIEWS_ID } from "../../enums/routing-enums";
import { NavData, NavDropdown, NavDropdownItem } from "../../interfaces/routing";
import { BootUser } from "../../../reducers/boot";
import { Config } from "../../interfaces/config";
import { getIncentiveViewID } from "../IncentivesViewID/IncentiveViewID";
import { filterDropdown } from "./createMenuConfig";
import { ANZ_MARKET_CODES, ESAN_MARKET_CODES, MSB_MARKET_CODES } from "../../constants/market-constants";
import { MARKET_CODES } from "../../enums/market-enums";

export const getPerformancePlusPath = (configuration: Config, currentPeriod?: string, userCountry?: string): string => {
  if (!currentPeriod || !userCountry) {
    return ROUTE_PATHS.PPE;
  }
  const viewId = getIncentiveViewID(AWARD_CODES.PERFORMANCE_PLUS, currentPeriod, configuration, userCountry);
  return viewId === ROUTE_VIEWS_ID.PPV2_VIEW_ID ? ROUTE_PATHS.PERFORMANCE_PLUS : ROUTE_PATHS.PPE;
};

export const createLeaderGrowthDropdown = (
  navConfigs: NavData[],
  { user, configuration }: { user: BootUser; configuration: Config },
  currentPeriod?: string,
  shouldShowPGTI = false,
): NavDropdown => {
  const { performanceLabel, performanceCode } = getPerformanceIncentive(navConfigs);

  const performancePath = getPerformancePlusPath(configuration, currentPeriod, user?.isoCountryCode);

  const annualPGG = getAnnualGroupGrowthConfigs(navConfigs, configuration, user, currentPeriod, shouldShowPGTI);

  const leaderGrowthDropdown = {
    label: DROPDOWN_LABELS.LEADER_GROWTH,
    key: DROPDOWN_KEYS.LEADER_GROWTH,
    items: [
      {
        label: performanceLabel,
        path: performancePath,
        class: performancePath,
        code: performanceCode,
        navPosition: 0,
      },
      {
        label: ROUTE_LABELS.MONTHLY_PGG,
        path: ROUTE_PATHS.MONTHLY_PGG,
        class: ROUTE_PATHS.MONTHLY_PGG,
        code: AWARD_CODES.MONTHLY_PGG,
        navPosition: 1,
      },
      {
        label: ROUTE_LABELS.MONTHLY_Q,
        path: ROUTE_PATHS.MONTHLY_Q,
        class: ROUTE_PATHS.MONTHLY_Q,
        code: AWARD_CODES.MONTHLY_Q,
        navPosition: 2,
      },
      {
        label: annualPGG.label,
        path: annualPGG.path,
        class: annualPGG.class,
        code: annualPGG.code,
        navPosition: 3,
      },
      {
        label: ROUTE_LABELS.PERSONAL_GROUP_GROWTH,
        path: ROUTE_PATHS.PGG_GCR,
        class: ROUTE_PATHS.PGG_GCR,
        code: AWARD_CODES.PGG_GCR,
        navPosition: 4,
      },
      getFggView(user, configuration, currentPeriod),
      {
        label: ROUTE_LABELS.TTCI,
        path: ROUTE_PATHS.TWO_TIME_CASH_INCENTIVE,
        class: ROUTE_PATHS.TWO_TIME_CASH_INCENTIVE,
        code: AWARD_CODES.TWO_TIME_CASH_INCENTIVE,
        navPosition: 6,
      },
      {
        label: get100DiamondBonusLabel(user),
        path: ROUTE_PATHS.DIAMOND_BONUS,
        class: ROUTE_PATHS.DIAMOND_BONUS,
        code: AWARD_CODES.DIAMOND_BONUS,
        navPosition: 7,
      },
      {
        label: ROUTE_LABELS.HEALTH_WELLBEING,
        path: ROUTE_PATHS.HEALTH_WELLBEING,
        class: ROUTE_PATHS.HEALTH_WELLBEING,
        code: AWARD_CODES.HEALTH_WELLBEING,
        navPosition: 8,
      },
      {
        label: ROUTE_LABELS.HAT,
        path: ROUTE_PATHS.HAT,
        class: ROUTE_PATHS.HAT,
        code: AWARD_CODES.HAT,
        navPosition: 9,
      },
      {
        label: ROUTE_LABELS.GAR,
        path: ROUTE_PATHS.GAR_LEG_QC_DETAILS,
        class: ROUTE_PATHS.GAR_LEG_QC_DETAILS,
        code: AWARD_CODES.GAR,
        navPosition: 10,
      },
      {
        label: ROUTE_LABELS.FAA,
        path: ROUTE_PATHS.FAA,
        class: ROUTE_PATHS.FAA,
        code: AWARD_CODES.FAA,
        navPosition: 11,
      },
      {
        label: ROUTE_LABELS.VISION_BONUS,
        path: ROUTE_PATHS.VISION_BONUS,
        class: ROUTE_PATHS.VISION_BONUS,
        code: [AWARD_CODES.VISION_BONUS_TRACKING_YEAR, AWARD_CODES.VISION_BONUS_QUALIFICATION_YEAR],
        navPosition: 12,
      },
      {
        label: ROUTE_LABELS.QC_GROWTH,
        path: ROUTE_PATHS.QC_GROWTH,
        class: ROUTE_PATHS.QC_GROWTH,
        code: AWARD_CODES.QC_GROWTH,
        navPosition: 13,
      },
      {
        label: getLeadershipSeminarLabel(user),
        path: ROUTE_PATHS.LEADERSHIP_SEMINAR,
        class: ROUTE_PATHS.LEADERSHIP_SEMINAR,
        code: AWARD_CODES.LSI,
        navPosition: 14,
      },
    ],
  };

  return filterDropdown(navConfigs, leaderGrowthDropdown);
};

const isIncentiveActive = (navConfigs: NavData[], awardCode: number): boolean =>
  navConfigs.some((item) => item.awardNumber === awardCode && item.programActive);

const getAnnualGroupGrowthConfigs = (
  navConfigs: NavData[],
  configuration: Config,
  user: BootUser,
  currentPeriod?: string,
  shouldShowPGTI = false,
) => {
  if (shouldShowPGTI) {
    return {
      label: ROUTE_LABELS.PGTI,
      path: ROUTE_PATHS.ANNUAL_PGG,
      class: ROUTE_PATHS.ANNUAL_PGG,
      code: AWARD_CODES.PGTI,
    };
  }

  const annualActive = isIncentiveActive(navConfigs, AWARD_CODES.ANNUAL_PGG);
  const monthlyActive = isIncentiveActive(navConfigs, AWARD_CODES.MONTHLY_PGG);
  const defaultLabel = annualActive && monthlyActive ? ROUTE_LABELS.ANNUAL_PGG : ROUTE_LABELS.PERSONAL_GROUP_GROWTH;

  // Check if should use Annual Q view
  const isAnnualQ = getIsAnnualQIncentive(configuration, user, currentPeriod);

  const label = isAnnualQ ? ROUTE_LABELS.ANNUAL_Q : defaultLabel;
  const path = isAnnualQ ? ROUTE_PATHS.ANNUALQ : ROUTE_PATHS.ANNUAL_PGG;

  return { label, path, class: path, code: AWARD_CODES.ANNUAL_PGG };
};

const getLeadershipSeminarLabel = (user: BootUser) => {
  if (ESAN_MARKET_CODES.includes(user.aff) || ANZ_MARKET_CODES.includes(user.aff)) {
    return ROUTE_LABELS.LEADERSHIP_SEMINAR_ESAN;
  }
  return ROUTE_LABELS.LEADERSHIP_SEMINAR;
};

export const get100DiamondBonusLabel = (user: BootUser) => {
  if (user.aff === MARKET_CODES.HK) {
    return ROUTE_LABELS.A70_ELITE_BONUS;
  }
  if (MSB_MARKET_CODES.includes(user.aff)) {
    return ROUTE_LABELS.PATH_TO_A70;
  }
  return ROUTE_LABELS.DIAMOND_BONUS;
};

export const getIsAnnualQIncentive = (configuration: Config, user: BootUser, currentPeriod?: string) => {
  return (
    currentPeriod &&
    getIncentiveViewID(AWARD_CODES.ANNUAL_PGG, currentPeriod, configuration, user.isoCountryCode) ===
      ROUTE_VIEWS_ID.ANNUAL_Q
  );
};

export const getPerformanceIncentive = (
  navConfigs: NavData[],
): { performanceLabel: string; performanceCode: number } => {
  const performancePlusActive = isIncentiveActive(navConfigs, AWARD_CODES.PERFORMANCE_PLUS);
  const performanceEliteActive = isIncentiveActive(navConfigs, AWARD_CODES.PERFORMANCE_ELITE);

  if (performancePlusActive && performanceEliteActive) {
    return {
      performanceLabel: ROUTE_LABELS.PPE,
      performanceCode: AWARD_CODES.PERFORMANCE_ELITE,
    };
  }
  if (performanceEliteActive) {
    return {
      performanceLabel: ROUTE_LABELS.PERFORMANCE_ELITE,
      performanceCode: AWARD_CODES.PERFORMANCE_ELITE,
    };
  }
  return {
    performanceLabel: ROUTE_LABELS.PERFORMANCE_PLUS,
    performanceCode: AWARD_CODES.PERFORMANCE_PLUS,
  };
};

export const getFggView = (
  user: BootUser,
  configuration: Config,
  selectedPerformanceYearPeriod?: string,
): NavDropdownItem => {
  let fggItem = {
    label: ROUTE_LABELS.FGG,
    path: ROUTE_PATHS.FGG,
    class: ROUTE_PATHS.FGG,
    code: AWARD_CODES.FGG,
    navPosition: 5,
  };
  if (selectedPerformanceYearPeriod) {
    const overrideViewId = getIncentiveViewID(
      AWARD_CODES.FGG,
      selectedPerformanceYearPeriod,
      configuration,
      user.isoCountryCode,
    );
    if (overrideViewId === "frontlineQ") {
      fggItem = {
        label: ROUTE_LABELS.FRONTLINE_Q,
        path: ROUTE_PATHS.FRONTLINE_Q,
        class: ROUTE_PATHS.FRONTLINE_Q,
        code: AWARD_CODES.FGG,
        navPosition: 5,
      };
    } else if (overrideViewId === "leadershipPlus") {
      fggItem = {
        label: ROUTE_LABELS.LEADERSHIP_PLUS,
        path: ROUTE_PATHS.LEADERSHIP_PLUS,
        class: ROUTE_PATHS.LEADERSHIP_PLUS,
        code: AWARD_CODES.FGG,
        navPosition: 5,
      };
    }
  }
  return fggItem;
};
